import React, { FC, useMemo, useCallback, memo } from 'react';
import { useLazyQuery } from '@apollo/client';
import moment from 'moment';
import _map from 'lodash/map';
import _cloneDeep from 'lodash/cloneDeep';
import { gqlRoleStatusEnum as ROLE_STATUS } from '@minecraft.graphql-types';
import FormikSelectableSearchDropdown from '@minecraft.formik-selectable-search-dropdown';
import ErrorMessage from '@minecraft.error-message';
import { QUERY_PROJECT_ROLES } from '../../FilterBasic/FilterBasicCollection/FilterBasicCollectionGraphQL';
import { FilterData } from '../../shared/types';
import { Label, StyledFormikSelectableSearchDropdown } from '../../shared/styles';

type Props = FilterData & {
  projectId: number;
};

const Role: FC<Props> = ({ projectId, name, label }) => {
  const [getRoles, { data, error, loading }] = useLazyQuery(QUERY_PROJECT_ROLES, {
    variables: {
      projectId,
    },
  });

  const sortedRoles = useMemo(() => {
    if (!data?.projectRoles) return [];

    const clonedProjectRoles = _cloneDeep(data?.projectRoles);

    return clonedProjectRoles
      .filter((role) => ![ROLE_STATUS.ARCHIVED, ROLE_STATUS.ARCHIVE_IN_PROGRESS].includes(role.roleStatus.code))
      .sort((a, b): number => moment(b.created).unix() - moment(a.created).unix());
  }, [data?.projectRoles]);

  const handleRolesFetch = useCallback((): void => {
    if (!data) {
      getRoles();
    }
  }, [data]);

  if (error) {
    return <ErrorMessage error={error} />;
  }

  return (
    <>
      <Label>{label}</Label>
      <StyledFormikSelectableSearchDropdown
        saveAsOption
        name={name}
        isLoading={loading}
        onOptionsFetch={handleRolesFetch}
      >
        {_map(sortedRoles, (role) => (
          <FormikSelectableSearchDropdown.Option key={role.id} label={role.internalName || role.name} value={role.id} />
        ))}
      </StyledFormikSelectableSearchDropdown>
    </>
  );
};

export default memo(Role);
